import { useEffect, useState } from 'react';
import { CheckCircle2, Trash2 } from 'lucide-react';
import {
  MAX_TASK_REPEAT_INTERVAL,
  describeRepeat,
  type ChecklistItem,
  type ChecklistItemStatus,
  type TaskRepeatUnit,
  type UpdateChecklistItemInput,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { RecordConversations } from '../messaging/RecordConversations';
import { DueDatePicker } from './DueDatePicker';
import {
  EventOffsetField,
  draftToOffset,
  offsetToDraft,
  type OffsetDraft,
} from './EventOffsetField';
import { TASK_THREAD_ENTITY } from './TaskConversations';
import { TASK_STATUS_OPTIONS, type TaskAssignee } from './taskUi';

export interface TaskDetailModalProps {
  task: ChecklistItem | null;
  onClose: () => void;
  onSave: (updates: UpdateChecklistItemInput) => void;
  onDelete?: (task: ChecklistItem) => void;
  assignees: TaskAssignee[];
  assigneesLoading?: boolean;
  todayKey: string;
  /** A template task carries an offset from the event, not a date. */
  isTemplate?: boolean;
  eventStartAt?: Date | string | null;
  /** Where the task lives, shown under the title. */
  context?: string;
  saving?: boolean;
}

interface FormState {
  title: string;
  status: ChecklistItemStatus;
  assignedToUserId: string;
  dueDate: string;
  offset: OffsetDraft;
  notes: string;
  repeatUnit: TaskRepeatUnit | '';
  repeatInterval: string;
}

const REPEAT_OPTIONS: { id: TaskRepeatUnit | ''; label: string }[] = [
  { id: '', label: 'Does not repeat' },
  { id: 'DAY', label: 'Daily' },
  { id: 'WEEK', label: 'Weekly' },
  { id: 'MONTH', label: 'Monthly' },
];

const REPEAT_NOUN: Record<TaskRepeatUnit, string> = { DAY: 'days', WEEK: 'weeks', MONTH: 'months' };

/**
 * The gap typed, when it is a whole number in range; null otherwise. Never
 * rounded or clamped into one — a repeat saved at a cadence other than the
 * one typed is worse than being told the number will not do.
 */
function repeatGap(value: string): number | null {
  const text = value.trim();
  if (!/^\d+$/.test(text)) return null;
  const n = Number(text);
  return n >= 1 && n <= MAX_TASK_REPEAT_INTERVAL ? n : null;
}

function toForm(task: ChecklistItem): FormState {
  return {
    title: task.title,
    status: task.status,
    assignedToUserId: task.assignedToUserId ?? '',
    dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '',
    offset: offsetToDraft(task.dueDateOffsetDays),
    notes: task.notes ?? '',
    repeatUnit: task.repeatUnit ?? '',
    repeatInterval: String(task.repeatInterval ?? 1),
  };
}

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Everything about one task in one place: title, status, who, when, notes —
 * and who finished it. Opens from any row, in any view.
 */
export function TaskDetailModal({
  task,
  onClose,
  onSave,
  onDelete,
  assignees,
  assigneesLoading,
  todayKey,
  isTemplate = false,
  eventStartAt,
  context,
  saving = false,
}: TaskDetailModalProps) {
  const [form, setForm] = useState<FormState | null>(task ? toForm(task) : null);

  useEffect(() => {
    setForm(task ? toForm(task) : null);
  }, [task]);

  if (!task || !form) return null;

  const gap = form.repeatUnit ? repeatGap(form.repeatInterval) : null;
  const gapInvalid = !!form.repeatUnit && gap === null;
  const offset = isTemplate ? draftToOffset(form.offset) : null;
  const offsetInvalid = offset === undefined;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  function save() {
    if (!form || !task || !form.title.trim() || gapInvalid || offsetInvalid) return;
    onSave({
      title: form.title.trim(),
      status: form.status,
      assignedToUserId: form.assignedToUserId || null,
      notes: form.notes.trim() || null,
      repeatUnit: form.repeatUnit || null,
      repeatInterval: form.repeatUnit ? repeatGap(form.repeatInterval) : null,
      ...(isTemplate ? { dueDateOffsetDays: offset } : { dueDate: form.dueDate || null }),
    });
  }

  const completedAt = task.completedAt ? new Date(task.completedAt) : null;

  return (
    <EntityModal
      open
      onClose={onClose}
      title="Edit task"
      size="md"
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          {onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(task)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
            >
              <Trash2 size={14} /> Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!form.title.trim() || gapInvalid || offsetInvalid || saving}
              className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        className="space-y-4"
      >
        {context && <p className="text-xs text-gray-500 -mt-1">{context}</p>}
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Task</span>
          <input
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              }
            }}
            maxLength={500}
            className={fieldClass}
            autoFocus
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">Status</span>
            <SearchableListSelect
              items={TASK_STATUS_OPTIONS}
              value={form.status}
              onChange={(v) =>
                typeof v === 'string' && v && set('status', v as ChecklistItemStatus)
              }
              getValue={(o) => o.id}
              getLabel={(o) => o.label}
              ariaLabel="Status"
            />
          </div>
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">Assigned to</span>
            <SearchableListSelect
              items={assignees}
              value={form.assignedToUserId || null}
              onChange={(v) => set('assignedToUserId', typeof v === 'string' ? v : '')}
              getValue={(a) => a.id}
              getLabel={(a) => a.name}
              placeholder="Nobody"
              clearable
              isLoading={assigneesLoading}
              ariaLabel="Assigned to"
            />
          </div>
        </div>

        {isTemplate ? (
          <EventOffsetField value={form.offset} onChange={(v) => set('offset', v)} />
        ) : (
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">Due</span>
            <DueDatePicker
              value={form.dueDate}
              onChange={(v) => set('dueDate', v)}
              todayKey={todayKey}
              eventStartAt={eventStartAt}
              placeholder="No due date"
            />
          </div>
        )}

        <div>
          <span className="block text-xs font-medium text-gray-600 mb-1">Repeat</span>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[10rem]">
              <SearchableListSelect
                items={REPEAT_OPTIONS}
                value={form.repeatUnit}
                onChange={(v) =>
                  set('repeatUnit', (typeof v === 'string' ? v : '') as FormState['repeatUnit'])
                }
                getValue={(o) => o.id}
                getLabel={(o) => o.label}
                ariaLabel="Repeat"
              />
            </div>
            {form.repeatUnit && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                every
                <input
                  type="number"
                  min={1}
                  max={MAX_TASK_REPEAT_INTERVAL}
                  value={form.repeatInterval}
                  onChange={(e) => set('repeatInterval', e.target.value)}
                  aria-label="Repeat every"
                  className="w-16 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {REPEAT_NOUN[form.repeatUnit]}
              </label>
            )}
          </div>
          {gapInvalid ? (
            <p role="alert" className="mt-1 text-xs text-red-700">
              {`Repeat every whole number of ${REPEAT_NOUN[form.repeatUnit as TaskRepeatUnit]} from 1 to ${MAX_TASK_REPEAT_INTERVAL}.`}
            </p>
          ) : (
            form.repeatUnit && (
              <p className="mt-1 text-xs text-gray-500">
                {!isTemplate && !form.dueDate
                  ? 'Give it a due date — each next one is dated from it.'
                  : `${describeRepeat(form.repeatUnit, gap)} from its due date. When it is done, the next one is added to this list.`}
              </p>
            )
          )}
        </div>

        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Notes</span>
          <textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={3}
            maxLength={2000}
            className={fieldClass}
          />
        </label>

        <div className="text-xs text-gray-500 space-y-1">
          {completedAt && (
            <p className="flex items-center gap-1 text-green-700">
              <CheckCircle2 size={13} />
              Done{task.completedByName ? ` by ${task.completedByName}` : ''} ·{' '}
              {completedAt.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          )}
          {task.createdByName && <p>Added by {task.createdByName}</p>}
        </div>
      </form>
      {/*
        Outside the form: the conversation's own buttons are not the task's
        Save, and Enter in its composer must not submit the task.
      */}
      {!isTemplate && (
        <div className="mt-4 pt-4 border-t border-gray-100" data-testid="task-conversation">
          <RecordConversations
            entityType={TASK_THREAD_ENTITY}
            entityId={task.id}
            label="Discuss this task"
          />
        </div>
      )}
    </EntityModal>
  );
}
